import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../lib/api.js";
import { ArrowLeft, Mail, Send, Eye, Loader2, Users, CheckCircle2, AlertCircle } from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";

const AUDIENCES = [
  { k: "active", label: "Utilizadores activos" },
  { k: "all",    label: "Todos (incl. desactivados)" },
  { k: "admins", label: "Apenas administradores" },
];

export default function BroadcastEmailPage() {
  const [form, setForm] = useState({ subject: "", body_markdown: "", audience: "active", branch: "", role: "", dry_run_to: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try { setHistory((await api.get("/admin/broadcast/history?limit=10")).data); } catch {}
  };
  useEffect(() => { loadHistory(); }, []);

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const doPreview = async () => {
    setErr(""); setPreview(null); setBusy(true);
    try {
      const r = await api.post("/admin/broadcast/preview", {
        subject: form.subject, body_markdown: form.body_markdown,
        audience: form.audience, branch: form.branch || null, role: form.role || null,
      });
      setPreview(r.data);
    } catch (e) { setErr(formatError(e.response?.data?.detail) || "Falha a gerar pré-visualização"); }
    finally { setBusy(false); }
  };

  const doSend = async ({ dryRun = false } = {}) => {
    if (!window.confirm(dryRun
      ? `Enviar apenas para ${form.dry_run_to} (teste)?`
      : `Enviar comunicado a ${preview?.recipient_count || "?"} destinatários? Esta acção não pode ser desfeita.`)) return;
    setErr(""); setSent(null); setBusy(true);
    try {
      const payload = {
        subject: form.subject, body_markdown: form.body_markdown,
        audience: form.audience, branch: form.branch || null, role: form.role || null,
      };
      if (dryRun) payload.dry_run_to = form.dry_run_to;
      const r = await api.post("/admin/broadcast/send", payload);
      setSent(r.data);
      await loadHistory();
    } catch (e) { setErr(formatError(e.response?.data?.detail) || "Falha a enviar"); }
    finally { setBusy(false); }
  };

  const canSubmit = form.subject.trim().length >= 3 && form.body_markdown.trim().length >= 10;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-screen-xl mx-auto">
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-ronil-navy mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> Admin
      </Link>
      <PageHeader
        overline="Administração"
        title="Comunicado por email"
        subtitle="Envia um email institucional a toda a equipa (ou a um grupo específico). Suporta Markdown. Testa com dry-run antes de enviar a todos."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* FORM */}
        <section className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
          <div className="flex items-center gap-2 text-ronil-navy font-heading font-bold">
            <Mail className="w-4 h-4" /> Mensagem
          </div>

          <Field label="Assunto *">
            <input className={inputCls} value={form.subject} onChange={(e) => upd("subject", e.target.value)} placeholder="Ex.: Novidades da plataforma Ronil Command Center" data-testid="bcast-subject" />
          </Field>

          <Field label="Corpo (Markdown suportado) *">
            <textarea rows={12} className={inputCls} value={form.body_markdown} onChange={(e) => upd("body_markdown", e.target.value)} placeholder="# Olá equipa&#10;&#10;Já podem usar o módulo **Logística**. Tudo pronto!&#10;&#10;- Documentos&#10;- Inspecções&#10;- Alertas&#10;&#10;Qualquer dúvida, falem comigo." data-testid="bcast-body" />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Audiência">
              <select className={inputCls} value={form.audience} onChange={(e) => upd("audience", e.target.value)} data-testid="bcast-audience">
                {AUDIENCES.map(a => <option key={a.k} value={a.k}>{a.label}</option>)}
              </select>
            </Field>
            <Field label="Filtrar por filial">
              <input className={inputCls} value={form.branch} onChange={(e) => upd("branch", e.target.value)} placeholder="Ex.: Maputo" />
            </Field>
          </div>

          <Field label="Teste primeiro — enviar apenas para">
            <div className="flex gap-2">
              <input className={inputCls} value={form.dry_run_to} onChange={(e) => upd("dry_run_to", e.target.value)} placeholder="o-teu-email@ronil-auto.co.mz" data-testid="bcast-dryrun" />
              <button type="button" onClick={() => doSend({ dryRun: true })} disabled={!canSubmit || !form.dry_run_to || busy} className="bg-white border border-ronil-navy text-ronil-navy px-3 py-1.5 text-sm font-heading font-semibold rounded-md disabled:opacity-40">
                Enviar teste
              </button>
            </div>
          </Field>

          <div className="flex items-center gap-2 pt-2">
            <button onClick={doPreview} disabled={!canSubmit || busy} className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-md text-sm font-heading font-semibold disabled:opacity-50" data-testid="bcast-preview">
              <Eye className="w-4 h-4" /> Pré-visualizar
            </button>
            <button onClick={() => doSend({ dryRun: false })} disabled={!preview || busy} className="inline-flex items-center gap-1.5 bg-ronil-navy hover:bg-ronil-navy-hover text-white px-4 py-2 rounded-md text-sm font-heading font-semibold disabled:opacity-50" data-testid="bcast-send">
              <Send className="w-4 h-4" /> Enviar a {preview?.recipient_count ?? "…"} pessoas
            </button>
          </div>
          {err && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>}
          {sent && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 inline-flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> Enviado: {sent.sent_count} · Falhas: {sent.failed_count}
            </div>
          )}
        </section>

        {/* PREVIEW */}
        <section className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-ronil-navy font-heading font-bold">
              <Eye className="w-4 h-4" /> Pré-visualização
            </div>
            {preview && (
              <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                <Users className="w-3.5 h-3.5" /> {preview.recipient_count} destinatários
              </span>
            )}
          </div>
          {busy ? <Spinner /> : !preview ? (
            <div className="bg-slate-50 border border-dashed border-slate-300 rounded p-10 text-center text-slate-400 text-sm">
              Clica em "Pré-visualizar" para veres a versão final do email.
            </div>
          ) : (
            <>
              <div className="mb-2 text-xs text-slate-500">Primeiros destinatários: {preview.recipient_sample.join(", ")}{preview.recipient_count > preview.recipient_sample.length && "…"}</div>
              <iframe title="preview" srcDoc={preview.html} className="w-full h-[560px] border border-slate-200 rounded-md" />
            </>
          )}
        </section>
      </div>

      {/* HISTORY */}
      <section className="mt-6">
        <h2 className="font-heading text-sm font-bold text-ronil-navy mb-2 uppercase tracking-widest">Histórico de envios</h2>
        {history.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-6 text-center text-slate-400 text-sm">Ainda não enviaste nenhum comunicado.</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Data</th>
                  <th className="px-3 py-2 text-left">Assunto</th>
                  <th className="px-3 py-2 text-left">Audiência</th>
                  <th className="px-3 py-2 text-right">Enviados</th>
                  <th className="px-3 py-2 text-right">Falhas</th>
                  <th className="px-3 py-2 text-left">Por</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 text-xs">{(h.sent_at || "").replace("T", " ").slice(0, 16)}</td>
                    <td className="px-3 py-1.5 text-xs">{h.subject}{h.dry_run && <span className="ml-1 text-[9px] uppercase font-heading tracking-widest text-amber-700">teste</span>}</td>
                    <td className="px-3 py-1.5 text-xs">{h.audience}</td>
                    <td className="px-3 py-1.5 text-xs text-right tabular-nums">{h.sent_count}</td>
                    <td className="px-3 py-1.5 text-xs text-right tabular-nums text-rose-600">{h.failed_count || "—"}</td>
                    <td className="px-3 py-1.5 text-xs">{h.sent_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest font-heading font-semibold text-slate-500">{label}</span>
      {children}
    </label>
  );
}
function Spinner() { return <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> A gerar…</div>; }

const inputCls = "w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm focus:border-ronil-navy focus:outline-none focus:ring-2 focus:ring-ronil-navy/10";
